import React from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Breadcrumbs, 
  Link,
  Divider,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';

// Styled Components
const StyledLegalSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 0),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
}));

const StyledLegalBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledLegalPaper = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  backgroundColor: $isDarkMode ? '#3D3D3D' : theme.palette.background.paper,
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5),
  },
}));

const StyledLegalDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledLegalContent = styled(Box)(({ theme }) => ({
  typography: 'body1',
}));

interface LegalPageProps {
  title: string;
  lastUpdated?: string;
  content: React.ReactNode;
}

export const LegalPage: React.FC<LegalPageProps> = ({ 
  title, 
  lastUpdated, 
  content 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <StyledLegalSection component="section">
      <Container maxWidth="md">
        {/* Breadcrumbs navigation */}
        <StyledLegalBreadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
        >
          <Link 
            component={RouterLink} 
            to="/"
            underline="hover"
            color="inherit"
          >
            Home
          </Link>
          <Typography color="text.primary">{title}</Typography>
        </StyledLegalBreadcrumbs>

        <StyledLegalPaper elevation={3} $isDarkMode={isDarkMode}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            color="primary.main"
          >
            {title}
          </Typography>
          
          <StyledLegalDivider />
          
          <StyledLegalContent>
            {content}
          </StyledLegalContent>
        </StyledLegalPaper>
      </Container>
    </StyledLegalSection>
  );
}; 